import React, { useState, useEffect } from "react";
import { Button, Checkbox, TimePicker, message, Card, Spin } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { EditOutlined } from "@ant-design/icons";
import { token } from "../../constants/Constants";
import { createWorkingHours, getOrderSlots, updateWorkingHours } from "../../endpoints/API";

const WorkingHours = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null); // Track editing


  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const apiUrl = prodTestUrl || baseUrl;

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    setFetchingSlots(true);
    try {
      const response = await getOrderSlots();
      if (response.statusText == "OK") {
        const data = response?.data;
        setSlots(data);
      } else {
        message.error("Failed to fetch order slots.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"Error fetching order slots.");
    } finally {
      setFetchingSlots(false);
    }
  };

  const onChangeCheckbox = (e) => {
    setIsEnabled(e.target.checked);
    if (!e.target.checked) {
      setStartTime(null);
      setEndTime(null);
    }
  };

  const onChangeStartTime = (time, timeString) => {
    setStartTime(time ? moment(timeString, "h:mm A") : null);
  };

  const onChangeEndTime = (time, timeString) => {
    setEndTime(time ? moment(timeString, "h:mm A") : null);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const payload = {
      isEnabled,
      startTime: startTime ? startTime.format("HH:mm") : null,
      endTime: endTime ? endTime.format("HH:mm") : null,
    };

    try {
      let response;
      if (editingSlot) {
        response = await updateWorkingHours(editingSlot.id, payload);
      } else {
        response = await createWorkingHours(payload);
      }

      if (response.statusText=="OK") {
        message.success(
          `Working hours ${editingSlot ? "updated" : "submitted"} successfully!`
        );
        fetchSlots();
        resetForm();
      } else {
        message.error("Failed to submit working hours.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"An error occurred while submitting.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsEnabled(false);
    setStartTime(null);
    setEndTime(null);
    setEditingSlot(null);
  };

  const isSubmitDisabled = !isEnabled || !startTime || !endTime;

  return (
    <div className="p-4 mt-[2rem] px-[5%]">
      {slots.length === 0 || editingSlot ? (
        <div className="border shadow-lg py-[3rem]">
          <div className="text-center text-4xl font-semibold underline mb-4">
            {editingSlot ? "Edit Working Hours" : "Working Hours"}
          </div>

          <div className="flex justify-center gap-2 text-xl">
            <span>Enable Working Hours Window? :</span>
            <Checkbox onChange={onChangeCheckbox} checked={isEnabled} />
          </div>

          <div className="text-center mt-[2rem] text-lg">
            Start Time:
            <TimePicker
              use12Hours
              format="h:mm A"
              onChange={onChangeStartTime}
              //   disabled={!isEnabled}
              value={startTime}
              className="ml-2"
            />
          </div>

          <div className="text-center mt-[2rem] text-lg">
            End Time:
            <TimePicker
              use12Hours
              format="h:mm A"
              onChange={onChangeEndTime}
              //   disabled={!isEnabled}
              value={endTime}
              className="ml-2"
            />
          </div>

          <div className="mt-[2rem] text-center">
            {editingSlot && (
              <Button className="mr-2" onClick={resetForm}>
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              //   disabled={isSubmitDisabled}
              loading={loading}
              onClick={handleSubmit}
            >
              {editingSlot ? "Update" : "Submit"}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-2xl font-semibold mb-4 underline">
            Selected Order Slots
          </div>
          {fetchingSlots ? (
            <Spin />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slots?.map((slot) => (
                <Card
                  key={slot.id}
                  title={
                    <div className="flex items-center">
                      <div className="text-xl">Slot</div>
                      <div className="ml-auto text-xl">
                        <EditOutlined
                          className="cursor-pointer"
                          onClick={() => {
                            setIsEnabled(
                              slot.status === "enable" ? true : false
                            );
                            setStartTime(moment(slot.slot_start_time));
                            setEndTime(moment(slot.slot_end_time));
                            setEditingSlot(slot);
                          }}
                        />
                      </div>
                    </div>
                  }
                  bordered
                >
                  <p>
                    <strong>Working Hour Window Enabled:</strong>{" "}
                    {slot.status === "enable" ? "True" : "False"}
                  </p>
                  <p>
                    <strong>Start Time:</strong>{" "}
                    {moment(slot.slot_start_time).format("h:mm A")}
                  </p>
                  <p>
                    <strong>End Time:</strong>{" "}
                    {moment(slot.slot_end_time).format("h:mm A")}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkingHours;
